import React from 'react'
import { useTranslation } from 'react-i18next'
import { Video } from 'lucide-react'
import { useLocalizedHiringTemplateName } from 'hooks/useLocalizedHiringTemplateName'
import { cn } from 'lib/utils'

interface OfferRecruitmentProcessBarProps {
  hiringProcessName?: string | null
  onValidationInterviewsClick?: () => void
  validationActive?: boolean
}

const figmaRecruitmentBarClass =
  'flex h-14 w-full max-w-full min-w-0 items-center justify-between rounded-[6px] bg-[#DDE1F0]/50 p-4 dark:bg-muted/50'

const figmaRecruitmentTitleClass =
  "font-['Montserrat',sans-serif] text-sm font-medium leading-[1.5] tracking-[0.01em] text-[#232323] dark:text-foreground"

const figmaValidationInterviewsButtonClass =
  'inline-flex h-6 shrink-0 items-center gap-[10px] rounded-sm transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40'

const figmaValidationInterviewsButtonActiveClass =
  'rounded-md bg-[#E8F7F7] px-2 py-1 text-[#19A6A6] dark:bg-[#19A6A6]/15 dark:text-[#7dd9d9]'

/**
 * Full-width bar above the kanban — aligned with pipeline columns, not the back-button header row.
 */
export function OfferRecruitmentProcessBar({
  hiringProcessName,
  onValidationInterviewsClick,
  validationActive = false,
}: OfferRecruitmentProcessBarProps) {
  const { t } = useTranslation()
  const localizedHiringProcessName = useLocalizedHiringTemplateName(hiringProcessName)

  const prefixLabel = t('offerDetail.recruitmentProcess', 'Processus de recrutement')
  const fullTitle = localizedHiringProcessName
    ? `${prefixLabel} : ${localizedHiringProcessName}`
    : prefixLabel

  const validationLabel = t('offerDetail.validationInterviews', 'Interviews de validation')

  return (
    <div className={figmaRecruitmentBarClass}>
      <span className={cn(figmaRecruitmentTitleClass, 'min-w-0 flex-1 truncate')} title={fullTitle}>
        {prefixLabel}
        {localizedHiringProcessName ? (
          <>
            {' : '}
            <span className="font-bold">{localizedHiringProcessName}</span>
          </>
        ) : null}
      </span>
      <button
        type="button"
        onClick={onValidationInterviewsClick}
        className={cn(
          figmaValidationInterviewsButtonClass,
          'ml-3 shrink-0',
          validationActive && figmaValidationInterviewsButtonActiveClass
        )}
        aria-label={validationLabel}
        aria-pressed={validationActive}
        title={validationLabel}
      >
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
            validationActive
              ? 'bg-[#19A6A6] text-white'
              : 'bg-[#19A6A6]/15 text-[#19A6A6] dark:bg-[#19A6A6]/25 dark:text-[#7dd9d9]'
          )}
          aria-hidden
          title={validationLabel}
        >
          <Video className="h-3.5 w-3.5" strokeWidth={1.5} />
        </span>
        <span
          className={cn(
            figmaRecruitmentTitleClass,
            'text-xs leading-[1.5] tracking-normal'
          )}
        >
          {validationLabel}
        </span>
      </button>
    </div>
  )
}
